/**
 * Объявление про журнал. Всплывает у каждого вошедшего.
 * «Понятно» прячет его до следующего захода. «Больше не показывать» — навсегда.
 */

import { useEffect, useState, type ReactElement } from 'react';

import { useSession } from '../access/session';

import './statNotice.css';

const SEEN = 'forq.stat-notice.3';

export function StatNotice(): ReactElement | null {
  const id = useSession((s) => s.me.id);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    try {
      if (localStorage.getItem(SEEN) === '1' || sessionStorage.getItem(SEEN) === '1') return;
    } catch {
      return;
    }
    setOpen(true);
  }, [id]);

  if (!open) return null;

  const later = (): void => {
    try {
      sessionStorage.setItem(SEEN, '1');
    } catch {
      /* закроется до перезагрузки вкладки */
    }
    setOpen(false);
  };

  const dismiss = (): void => {
    try {
      localStorage.setItem(SEEN, '1');
    } catch {
      /* окно закроется; без хранилища оно вернётся на следующем заходе */
    }
    setOpen(false);
  };

  return (
    <div className="nx-root nx-statnote" role="dialog" aria-modal="true" aria-labelledby="nx-statnote-title">
      <article className="nx-statnote__sheet">
        <h1 id="nx-statnote-title">Обновление: Добавлен новый раздел для удобного ведения статистики и аналитики!</h1>
        <p>Переходим к фиксации результатов — раздел статистики полностью заменяет сторонние эксель-таблицы, считая ваш профит и винрейт на автомате</p>
        <p>Раздел называется «моя статистика» и находится в верхней шапке сайта сразу после вкладок ленты. Сюда складываются абсолютно все вилки, которые вы взяли в работу и зафиксировали.</p>

        <h2>БАЗОВЫЕ МЕТРИКИ И ГРАФИКИ</h2>
        <p>В самом верху раздела жестко висят два главных числа, которые не зависят от ваших фильтров по датам:</p>
        <figure>
          <img src="/stat/08-obzor.png" alt="Плюс за месяц, плюс всего, прошло, горка и круг контор" />
        </figure>
        <ul>
          <li>🟢 Плюс за месяц — ваш чистый профит в текущем месяце</li>
          <li>🟢 Плюс всего — общая сумма за всё время ведения журнала</li>
          <li>🟢 Прошло — ваш реальный винрейт (доля зашедших вилок из тех, что уже рассчитаны конторой)</li>
        </ul>
        <p>Ниже идет панель фильтров по датам: можно в один клик выбрать этот месяц, прошлый или все вилки. Если нужен конкретный день или неделя — просто выставляем рамки в полях «с» и «по» (день делается одинаковой датой в обоих полях, неделя от понедельника до воскресенья).</p>
        <p>Именно за этими датами едет вся остальная визуализация на странице:</p>
        <ul>
          <li>Горка — линия вашего накопленного профита, а не разрывные скачки по дням (навели на точку — увидели дату и общую кассу на этот момент)</li>
          <li>Круг контор — показывает, на каких площадках вы реально ставите чаще всего, а не где просто лежат деньги</li>
          <li>Пик — в какое время суток вы ловите максимум вилок (ночь, утро, день или вечер)</li>
        </ul>

        <h2>ДОБАВЛЕНИЕ ВИЛКИ ИЗ БУФЕРА</h2>
        <p>Самая удобная фича сканера, чтобы не вбивать названия команд, кэфы и суммы ручками. В системе есть кнопка «из просмотренных», которая держит в короткой памяти 10 последних карточек, которые вы открывали в ленте.</p>
        <figure>
          <img src="/stat/08-prosmotrennye.png" alt="Десять последних вилок из ленты" />
        </figure>
        <p>Как правильно внести ставку в журнал за пару секунд:</p>
        <ul className="nx-statnote__steps">
          <li>1️⃣ Открываем карточку вилки в ленте (пока не открыли карточку, буфер её не запомнит)</li>
          <li>2️⃣ Переходим в раздел «моя статистика» и жмем «из просмотренных»</li>
          <li>3️⃣ Выбираем нужную вилку из списка (обычно она висит самой первой)</li>
          <li>4️⃣ Жестко сверяем кэф и сумму с тем, что РЕАЛЬНО приняли конторы — если цифры по факту отличаются, вписываем их руками, плюс пересчитается сам</li>
          <li>5️⃣ Жмем ГОТОВО</li>
        </ul>
        <figure>
          <img src="/stat/08-zapolneno.png" alt="Форма вилки с заметкой" />
        </figure>
        <p>Если вы брали связку без сканера или буфер пуст, вилку можно вбить руками через классическую форму. Там же можно прикрепить короткую заметку или кинуть прямую ссылку на матч в конторе.</p>

        <h2>ЖУРНАЛ И СТАТУСЫ</h2>
        <p>Вся ваша история падает в нижнюю таблицу.</p>
        <figure>
          <img src="/stat/08-zhurnal.png" alt="Таблица журнала со статусами" />
        </figure>
        <p>Каждая строка хранит полную базу: дата, матч, спорт, суммы по плечам, процент и итоговый плюс в рублях. Над таблицей висят фильтры статусов и сортировка (по дате, плюсу или проценту).</p>
        <p>НО пока вы не нажмете статус завершения, вилка будет висеть в состоянии «ждут» и не пойдет в итоговый профит.</p>
        <p>Как только матч закончился, вы сами отмечаете исход кликом по строке:</p>
        <ul>
          <li>Зашло / Не зашло — статус финального расчета</li>
          <li>Правка — возвращает строку в форму, если вы ошиблись при вводе цифр</li>
          <li>Корзина — удаляет мусорную или отмененную строку</li>
        </ul>

        <h2>ИМПОРТ СТАРЫХ ТАБЛИЦ</h2>
        <p>Если вы до этого вели учет в Google Таблицах или Excel, переносить руками сотни строк не придется.</p>
        <figure>
          <img src="/stat/08-import.png" alt="Импорт таблицы и ссылка на Google" />
        </figure>
        <p>В сканере есть автоматический импорт вашей старой базы:</p>
        <ul>
          <li>🟢 Файл — жмем «импорт таблицы» и загружаем классический .xlsx или .csv</li>
          <li>🟢 Google Таблица — просто вставляете ссылку на ваш док и жмете «открыть»</li>
        </ul>
        <p>Алгоритм покажет, сколько строк он нашел. Если всё корректно и данные ваши — жмем «забрать в журнал».</p>
        <p>После этого вся ваша математика за прошлые месяцы подтянется в общую горку сканера, и вторую таблицу рядом вести больше не придется.</p>
        <p>На этом разбор статистики закончен, переносите свои базы в сканер и отслеживайте реальный профит в одном окне без лишних костылей:)</p>
        <div className="nx-statnote__acts">
          <button type="button" className="nx-statnote__ok" onClick={later}>Понятно</button>
          <button type="button" className="nx-statnote__later" onClick={dismiss}>Больше не показывать</button>
        </div>
      </article>
    </div>
  );
}
